import { Injector, ViewChild, Directive, signal } from '@angular/core';
import { MatSort, Sort } from '@angular/material/sort';
import { ILocalSearchParameter } from '../models/models';
import { FiDataSourceBase, ILoadDataArgs } from '@fi-shared/fi-items-list/fi-data-source-base.directive';
import { ILightWeightObjectCollection } from '@fi-shared/models';
import { IItemsListDataFetchResult, IItemsListSort } from '@fi-shared/fi-items-list/fi-items-list';

@Directive()
export abstract class FiRemoteDataSource<T> extends FiDataSourceBase<T> {
    @ViewChild(MatSort, { static: false }) matSort: MatSort;

    localSearchParameter: ILocalSearchParameter;
    loadingCounter = signal(0);

    private _lightWeightObjectCollection = signal<ILightWeightObjectCollection | undefined>(undefined);
    public setLightWeightObjectCollection(lightWeightObjectCollection: ILightWeightObjectCollection | undefined) {
        this._lightWeightObjectCollection.set(lightWeightObjectCollection);
    }
    public lightWeightObjectCollection = this._lightWeightObjectCollection.asReadonly();

    /*
    method for fetching all list data by single request (items part, total count, data for local search)
    */
    abstract loadListDataFn(loadArgs?: ILoadDataArgs): Promise<IItemsListDataFetchResult<T>>;

    protected constructor(protected injector: Injector) {
        super(injector, {});
    }

    loadItemsFn(loadArgs: ILoadDataArgs): Promise<T[]> {
        this.loadingCounter.update((counter) => counter + 1);
        return new Promise<T[]>((resolve, reject) => {
            const loadItemsPromise = this.loadListDataFn(loadArgs).then((result) => {
                if (!loadArgs.fetchOnlyItems) {
                    //this.setTotalCount(result.TotalCount);
                    if (result.LightWeightObjectsForLocalSearch) {
                        this.setLightWeightObjectCollection(result.LightWeightObjectsForLocalSearch);
                    }
                }
                this.setTotalCount(
                    result.Items.length ? Number.MAX_SAFE_INTEGER : this.items.length + result.Items.length,
                );
                resolve(result.Items);
            }, reject);
        });
    }

    onScrollToBottom() {
        return this.loadNext();
    }

    //region Sorting
    onSortChanged($event: Sort) {
        this.reloadData({ fetchOnlyItems: true });
    }

    protected getMatSortInstance(): MatSort {
        return this.matSort;
    }

    protected getSelectedSort(): IItemsListSort {
        // for some cases (areas list for example), we should receive matSort from other component
        const matSort = this.getMatSortInstance();
        if (matSort) {
            return {
                ColumnName: matSort.active,
                SortDirection: matSort.direction === 'desc',
            };
        }
        return null;
    }

    //endregion

    //region Local search
    mergeLocalSearchParameter(parameter: ILocalSearchParameter) {
        if (this.localSearchParameter) {
            parameter.Names = this.localSearchParameter.Names;
            parameter.CreatedByUserNames = this.localSearchParameter.CreatedByUserNames;
            parameter.CreatedByUserIds = this.localSearchParameter.CreatedByUserIds;
            parameter.TagIds = (parameter.TagIds || []).concat(this.localSearchParameter.TagIds || []); // special case, TagIds can be initialized earlier
            parameter.BusinessAreaIds = this.localSearchParameter.BusinessAreaIds;
            parameter.RoleIds = this.localSearchParameter.RoleIds;
            parameter.BusinessUnitIds = this.localSearchParameter.BusinessUnitIds;
            parameter.AreaIds = this.localSearchParameter.AreaIds;
            parameter.ClusterIds = this.localSearchParameter.ClusterIds;
            parameter.Contacts = this.localSearchParameter.Contacts;
            parameter.Addresses = this.localSearchParameter.Addresses;
            parameter.ChecklistNames = this.localSearchParameter.ChecklistNames;
            parameter.MediaBankFileExtensions = this.localSearchParameter.MediaBankFileExtensions;
            parameter.ContentSearchTexts = this.localSearchParameter.ContentSearchTexts;
            parameter.ObjectIds = this.localSearchParameter.ObjectIds;
            parameter.ObjectTypes = this.localSearchParameter.ObjectTypes;
        }
    }

    onSearchChanged($event: ILocalSearchParameter) {
        this.reloadData();
    }
    //endregion
}
